// Research client hooks — mailbox investigations.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

export interface ResearchSummary {
  id: number;
  question: string;
  status: 'queued' | 'running' | 'done' | 'failed';
  progress: string | null;
  shared: boolean;
  error: string | null;
  mine: boolean;
  author: string;
  accountEmail: string | null;
  accountIds: number[];
  stats: { scannedMessages?: number; matchingMessages?: number; threads?: number; tookMs?: number; addresses?: string[] } | null;
  createdAt: string;
  finishedAt: string | null;
}

export interface ReportSection {
  heading: string;
  body?: string;
  table?: { columns: string[]; rows: string[][] } | null;
  threadIds?: number[];
}

export interface ResearchDetail extends Omit<ResearchSummary, 'author' | 'accountEmail'> {
  report: { title?: string; sections?: ReportSection[] } | null;
}

async function jfetch<T>(url: string, init?: RequestInit): Promise<T> {
  const r = await fetch(url, { credentials: 'include', ...init });
  if (!r.ok) {
    const j = await r.json().catch(() => ({}));
    throw new Error((j as { error?: string }).error || `HTTP ${r.status}`);
  }
  return r.json() as Promise<T>;
}

export function useResearchList() {
  return useQuery<{ reports: ResearchSummary[] }>({
    queryKey: ['research'],
    queryFn: () => jfetch('/api/research'),
    // Poll while anything is in flight so status/progress lines move.
    refetchInterval: (query) =>
      query.state.data?.reports.some((r) => r.status === 'queued' || r.status === 'running')
        ? 3000
        : false,
  });
}

export function useResearchReport(id: number | null) {
  return useQuery<ResearchDetail>({
    queryKey: ['research', id],
    enabled: id != null,
    queryFn: () => jfetch(`/api/research/${id}`),
    refetchInterval: (query) => {
      const s = query.state.data?.status;
      return s === 'queued' || s === 'running' ? 3000 : false;
    },
  });
}

export function useStartResearch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { accountIds: number[]; question: string }) =>
      jfetch<{ id: number }>('/api/research', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(v),
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['research'] }),
  });
}

export function useShareResearch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: number; shared: boolean }) =>
      jfetch(`/api/research/${v.id}`, {
        method: 'PATCH',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ shared: v.shared }),
      }),
    onSuccess: (_d, v) => {
      void qc.invalidateQueries({ queryKey: ['research'] });
      void qc.invalidateQueries({ queryKey: ['research', v.id] });
    },
  });
}

export function useDeleteResearch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => jfetch(`/api/research/${id}`, { method: 'DELETE' }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['research'] }),
  });
}
